import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MAX_QUOTE_COMPANIES, QUOTE_INPUT_KEY, buildQuoteRequest, penceToDecimal, summariseQuote, toCsv } from '@/lib/orderQuote';
import { OWN_IXBRL_ID, OWN_IXBRL_SERVICE } from '@/lib/ownIxbrl';
import { formatRegionMoneyMinor, getActiveRegionConfig } from '@/lib/regionConfig';
import { TAX_COMPUTATION_ID, TAX_COMPUTATION_SERVICE, orderedServiceIds } from '@/lib/taxComputation';
import { useOrderQuote } from '@/lib/useOrderQuote';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const FREE_SERVICES = { [TAX_COMPUTATION_ID]: TAX_COMPUTATION_SERVICE, [OWN_IXBRL_ID]: OWN_IXBRL_SERVICE };

// Only the quote prints: everything else on the page is hidden for Print / Save as PDF.
const PRINT_STYLE = `@media print {
  body * { visibility: hidden; }
  #price-quote, #price-quote * { visibility: visible; }
  #price-quote { position: absolute; left: 0; top: 0; width: 100%; }
  .no-print { display: none !important; }
}`;

const readQuoteInput = () => {
  try {
    return JSON.parse(sessionStorage.getItem(QUOTE_INPUT_KEY) || 'null');
  } catch (err) {
    return null;
  }
};

const QuotePage = () => {
  const navigate = useNavigate();
  const regionConfig = getActiveRegionConfig();
  const [input, setInput] = useState(null);
  const [companyCount, setCompanyCount] = useState(1);

  useEffect(() => {
    const saved = readQuoteInput();
    if (!saved) {
      toast.error('Choose your options first, then ask for a quote.');
      navigate('/wizard/start');
      return;
    }
    setInput(saved);
    setCompanyCount(Math.max(1, saved.selectedCompanies?.length || 1));
  }, [navigate]);

  const request = buildQuoteRequest(input, companyCount);
  const { quote, loading, error } = useOrderQuote(request);

  if (!input) return null;

  const namedCompanies = (input.selectedCompanies || []).length > 0;
  const money = (pence) => formatRegionMoneyMinor(pence, regionConfig);
  const summary = summariseQuote(quote);
  const delivery = regionConfig.delivery[input.config?.deliveryTime] || regionConfig.delivery.standard;
  const accountTypeLabel = (value) => regionConfig.accountTypes.find((type) => type.value === value)?.label || 'Your own iXBRL (no tagging)';
  const freeServiceNames = orderedServiceIds(input).filter((id) => FREE_SERVICES[id]).map((id) => FREE_SERVICES[id].title);
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const rows = (quote?.companies || []).map((company, index) => {
    const services = [
      ...company.lines.filter((line) => line.kind === 'tagging').map(() => 'iXBRL tagging'),
      ...company.lines.filter((line) => line.kind === 'filing').map((line) => line.service),
      ...freeServiceNames,
    ];
    return {
      number: index + 1,
      name: company.name,
      accountType: accountTypeLabel(company.account_type),
      services: services.join(', ') || '-',
      subtotal: company.subtotal_pence,
      discount: company.discount_pence,
      total: company.total_pence,
    };
  });

  const downloadCsv = () => {
    const currency = regionConfig.currency || '';
    const csvRows = [
      ['No', 'Company', 'Account type', 'Services', 'Turnaround', `List price (${currency})`, `Discount (${currency})`, `Total (${currency})`],
      ...rows.map((row) => [row.number, row.name, row.accountType, row.services, delivery.label, penceToDecimal(row.subtotal), penceToDecimal(row.discount), penceToDecimal(row.total)]),
      [],
      ['', '', '', '', 'Subtotal', penceToDecimal(summary.subtotal)],
      ['', '', '', '', `Volume discount (${summary.discountPercent}%)`, penceToDecimal(-summary.discount)],
      ['', '', '', '', 'Total', penceToDecimal(summary.total)],
    ];
    const url = URL.createObjectURL(new Blob([`﻿${toCsv(csvRows)}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `price-quote-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="quote-page">
      <style>{PRINT_STYLE}</style>
      <div className="page-header no-print">
        <h1 className="page-title">Price Quote</h1>
        <p className="page-subtitle">What your order would cost. No order is placed from this page.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="quote-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} disabled={!quote} data-testid="quote-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
          <Button onClick={() => window.print()} disabled={!quote} data-testid="quote-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print / Save as PDF
          </Button>
        </div>
      </div>

      {!namedCompanies && (
        <div className="bg-card border border-border rounded-lg p-4 mb-4 flex items-center gap-3 no-print">
          <label htmlFor="quote-company-count" className="text-sm font-medium">Number of companies</label>
          <Input
            id="quote-company-count"
            type="number"
            min={1}
            max={MAX_QUOTE_COMPANIES}
            value={companyCount}
            onChange={(e) => setCompanyCount(Math.min(MAX_QUOTE_COMPANIES, Math.max(1, parseInt(e.target.value, 10) || 1)))}
            className="w-24 h-9"
            data-testid="quote-company-count"
          />
          <span className="text-xs text-muted-foreground">Up to {MAX_QUOTE_COMPANIES} in one order. Volume discounts start at 3 companies.</span>
        </div>
      )}

      <section id="price-quote" className="bg-card border border-border rounded-lg p-6" data-testid="quote-document">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h2 className="font-heading text-lg font-semibold">Price Quote</h2>
          <span className="text-sm text-muted-foreground">{today}</span>
        </div>

        {!request && <p className="text-sm text-muted-foreground">Choose an account type first, and the quote will appear here.</p>}
        {error && <p className="text-sm text-red-700" data-testid="quote-error">Could not work out the price. {error}</p>}
        {request && loading && !quote && <p className="text-sm text-muted-foreground">Working out your price...</p>}

        {quote && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="quote-table">
              <thead>
                <tr className="text-left text-xs font-medium tracking-wider uppercase text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 w-8">#</th>
                  <th className="py-2 pr-3">Company</th>
                  <th className="py-2 pr-3">Account type</th>
                  <th className="py-2 pr-3">Services</th>
                  <th className="py-2 pr-3">Turnaround</th>
                  <th className="py-2 pr-3 text-right">List price</th>
                  <th className="py-2 pr-3 text-right">Discount</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.number} className="border-b border-border align-top">
                    <td className="py-3 pr-3 font-mono text-muted-foreground">{row.number}</td>
                    <td className="py-3 pr-3 font-medium">{row.name}</td>
                    <td className="py-3 pr-3">{row.accountType}</td>
                    <td className="py-3 pr-3 text-muted-foreground">{row.services}</td>
                    <td className="py-3 pr-3 whitespace-nowrap">{delivery.label}</td>
                    <td className="py-3 pr-3 text-right font-mono whitespace-nowrap">{money(row.subtotal)}</td>
                    <td className="py-3 pr-3 text-right font-mono whitespace-nowrap">{row.discount ? `-${money(row.discount)}` : '-'}</td>
                    <td className="py-3 text-right font-mono font-medium whitespace-nowrap">{money(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-sm">
                <tr>
                  <td colSpan={7} className="pt-3 pr-3 text-right text-muted-foreground">Subtotal</td>
                  <td className="pt-3 text-right font-mono whitespace-nowrap">{money(summary.subtotal)}</td>
                </tr>
                {summary.discount > 0 && (
                  <tr>
                    <td colSpan={7} className="pt-1 pr-3 text-right text-green-700">Volume discount ({summary.discountPercent}%)</td>
                    <td className="pt-1 text-right font-mono text-green-700 whitespace-nowrap">-{money(summary.discount)}</td>
                  </tr>
                )}
                <tr className="font-semibold">
                  <td colSpan={7} className="pt-2 pr-3 text-right">Total</td>
                  <td className="pt-2 text-right font-mono whitespace-nowrap" data-testid="quote-total">{money(summary.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {quote && (
          <p className="text-xs text-muted-foreground mt-4">
            {quote.region_code === 'UK' ? 'All prices are in pounds sterling. No VAT is added. ' : ''}
            Your invoice is calculated in the same way.
          </p>
        )}
      </section>
    </div>
  );
};

export default QuotePage;
